import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';

const WARNING_SECONDS = 120; // 2 minutes — matches SESSION_WARNING_MS

export default function SessionWarningModal() {
  const { sessionWarning, extendSession, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS);

  const handleLogout = async () => {
    await logout();
    navigate('/signin');
  };

  useEffect(() => {
    if (!sessionWarning) {
      setSecondsLeft(WARNING_SECONDS);
      return;
    }

    setSecondsLeft(WARNING_SECONDS);
    const interval = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [sessionWarning]);

  if (!sessionWarning) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const timeStr = minutes > 0
    ? `${minutes}:${String(seconds).padStart(2, '0')}`
    : `${seconds}s`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className={`${theme.card} rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
            <span className="text-yellow-600 text-xl">⏱</span>
          </div>
          <div>
            <h3 className={`font-semibold text-lg ${theme.text}`}>Session Expiring Soon</h3>
            <p className={`text-sm ${theme.textMuted}`}>You'll be logged out due to inactivity</p>
          </div>
        </div>

        <div className="mb-6 text-center">
          <span className={`text-4xl font-bold ${secondsLeft <= 30 ? 'text-red-500' : 'text-yellow-500'}`}>
            {timeStr}
          </span>
          <p className={`text-sm mt-1 ${theme.textSecondary}`}>remaining</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={extendSession}
            className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
          >
            Stay Logged In
          </button>
          <button
            onClick={handleLogout}
            className={`${btn.base} ${btn.md} ${theme.buttonGhost}`}
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}
